import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useDocumentTitle } from "@cinesys/ui/hooks";
import {
  Banner,
  Button,
  Input,
  Section,
  Select,
  Spinner,
} from "@cinesys/ui/components";
import { Check, RotateCcw, Search, X } from "react-feather";
import CustomerApiService from "../../../CustomerApiService";
import {
  buildComponents,
  ScrollBody,
  MetadataViewerSection,
} from "@cinesys/experience-ui/components";
import useMetadataSchemaEditor from "../hooks/useMetadataSchemaEditor";
import { loadLastSchema, saveLastValues } from "../hooks/useLastSchemaValues";

/**
 * `cs_metadata_viewer/components/CreatePlaceholderPage.tsx`, ported verbatim (plan §0, R1/R3)
 * with the usual auth/API-client/endpoint-shape swaps plus two genuine endpoint-shape deviations
 * `docs/decisions.md` records: Cantemo's `handleCreated` full-navigates to Portal's native
 * `/item/{itemId}/`; there is no such page here, so it navigates to the iconik asset URL the
 * backend now returns instead (`CreatePlaceholder.create`, `apps/customer-api`). Cantemo's
 * `handleCancel` falls back to Portal's `/home/browse`; this app's own item-browser index route
 * (`/item-browser`) is the equivalent "browse" landing spot.
 */
const LAST_VALUES_KEY = "cs_metadata_viewer_placeholder_last";

const PagePadding = styled.div`
  height: calc(100vh - 50px);
  padding: 20px;
  box-sizing: border-box;
  display: flex;
`;

const StyledSection = styled(Section)`
  height: 100%;
  flex: 1;
  min-width: 0;
  max-width: 1200px;
  margin: auto;
`;

const SchemaWrapper = styled.div`
  padding: 5px 15px;
  margin-bottom: 1px;
  border-bottom: 1px solid var(--dark100);
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 15px;
  border-top: 1px solid var(--dark400);
`;

const FooterRight = styled.div`
  display: flex;
  gap: 5px;
  margin-left: auto;
`;

/**
 * Field area + footer for the currently-selected schema. Mirrors ImportModal's ImportForm
 * (same `useMetadataSchemaEditor` hook, same field rendering, same Reset/Cancel/Submit footer
 * layout) — the only difference is the submit call: no file/entityId, and success navigates to
 * the newly-created item instead of notifying the job viewer.
 */
const PlaceholderForm = ({
  schema,
  search,
  onCreated,
  onCancel,
}: {
  schema: string;
  search: string;
  onCreated: (iconikUrl: string) => void;
  onCancel: () => void;
}) => {
  const [activeTabs, setActiveTabs] = useState<Record<string, string>>({});

  const {
    configQuery,
    data,
    syntheticTimespan,
    editControl,
    collapsedControl,
  } = useMetadataSchemaEditor({
    schema,
    itemId: "__placeholder__",
    schemaUrl: `/cs_metadata_viewer/placeholder/schema/${schema}`,
    queryKeyPrefix: "cs_metadata_viewer_placeholder",
    lastValuesStorageKey: LAST_VALUES_KEY,
    search,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      return CustomerApiService.fetch<{ itemId: string; iconikUrl: string }>(
        "POST",
        `/cs_metadata_viewer/placeholder`,
        {
          schema,
          metadata: editControl?.values ?? [],
        }
      );
    },
    onSuccess: ({ iconikUrl }) => {
      saveLastValues(LAST_VALUES_KEY, schema, editControl?.values ?? []);
      onCreated(iconikUrl);
    },
  });

  const handleReset = () => {
    editControl?.setValues([]);
  };

  const isResetNoop = !editControl?.valuesNoDefault.length;

  const components =
    data && editControl && syntheticTimespan
      ? buildComponents({
          timespans: [syntheticTimespan],
          search,
          editValues: editControl.values,
          data,
          activeTabs,
          collapsedControl,
          pendingGroups: editControl.pendingGroups,
          removedGroups: editControl.removedGroups,
          isEditing: editControl.isEditingKey("/"),
          canEdit: true,
          mode: "generic",
        })
      : [];

  if (configQuery.isError) {
    return <Banner error="Error loading schema configuration" padTop />;
  }

  if (configQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <ScrollBody>
        {components.map((section) => (
          <MetadataViewerSection
            key={section.editKey}
            data={data}
            section={section}
            search={search}
            collapsedControl={collapsedControl}
            activeTabs={activeTabs}
            setActiveTab={(s, tab) =>
              setActiveTabs((prev) => ({ ...prev, [s]: tab }))
            }
            editControl={editControl}
          />
        ))}
        {!components.length && (
          <Banner
            placeholder={
              search
                ? "No search results found"
                : "No fields configured for this schema"
            }
            padTop
          />
        )}
      </ScrollBody>
      {createMutation.isError && (
        <Banner error="Failed to create placeholder" padTop />
      )}
      <Footer>
        <Button
          label="Reset Metadata"
          icon={RotateCcw}
          type="danger"
          borderless
          onClick={handleReset}
          disabled={isResetNoop}
        />
        <FooterRight>
          <Button
            label="Cancel"
            icon={X}
            type="danger"
            borderless
            onClick={onCancel}
          />
          <Button
            label="Create Placeholder"
            icon={Check}
            type="success"
            loading={createMutation.isLoading || createMutation.isSuccess}
            disabled={
              !editControl?.canSave ||
              createMutation.isLoading ||
              createMutation.isSuccess
            }
            tooltip={
              editControl?.isError ? "Fix errors before creating" : undefined
            }
            onClick={() => createMutation.mutate()}
          />
        </FooterRight>
      </Footer>
    </>
  );
};

/**
 * Full-page counterpart to ImportModal's schema form: the same schema-driven metadata editor,
 * entered directly (no file selection) to create a bare iconik asset with no ingested file yet.
 * Mounted at `item-detail/placeholder/new` (see `AppRouter.tsx`) — the new "chrome" this port
 * genuinely needs (same carve-out `MetadataViewerPanel.tsx`'s header doc comment already
 * documents: Cantemo's own top-level wiring is Portal DOM-injection glue with nowhere to land in
 * a standalone SPA).
 */
const CreatePlaceholderPage = () => {
  const [schema, setSchema] = useState<string | undefined>();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useDocumentTitle("Create Placeholder");

  const schemasQuery = useQuery({
    queryKey: ["cs_metadata_viewer", "placeholder_schemas"],
    queryFn: () =>
      CustomerApiService.fetch<{
        schemas: { value: string; label: string }[];
        defaults: Record<string, string>;
      }>("GET", `/cs_metadata_viewer/placeholder/schemas`),
  });

  useEffect(() => {
    if (schemasQuery.data && !schema) {
      const lastSchema = loadLastSchema(LAST_VALUES_KEY);
      const isLastSchemaValid =
        lastSchema &&
        schemasQuery.data.schemas.some((s) => s.value === lastSchema);
      const nextSchema = isLastSchemaValid
        ? lastSchema
        : schemasQuery.data.defaults["item"];
      if (nextSchema) {
        setSchema(nextSchema);
      }
    }
  }, [schemasQuery.data, schema]);

  const handleCreated = (iconikUrl: string) => {
    // The asset lives in iconik, not this SPA — a full navigation is required.
    window.location.href = iconikUrl;
  };

  const handleCancel = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/item-browser");
    }
  };

  return (
    <PagePadding>
      <StyledSection
        noMargin
        title="Create Placeholder"
        buttons={
          <Input
            icon={Search}
            value={search}
            onChange={setSearch}
            placeholder="Search Metadata"
            clearable
            noPadding
            maxWidth={300}
            minWidth={180}
            height={35}
          />
        }
      >
        <SchemaWrapper>
          <Select
            label="Metadata Group"
            options={schemasQuery.data?.schemas}
            loading={schemasQuery.isLoading}
            onChange={({ value }) => setSchema(value)}
            value={schema}
            noPadding
            portalTarget={document.getElementById("cs_menu_portal")}
          />
        </SchemaWrapper>
        {schemasQuery.isError && (
          <Banner error="Error loading schemas" padTop />
        )}
        {schema && (
          <PlaceholderForm
            key={schema}
            schema={schema}
            search={search}
            onCreated={handleCreated}
            onCancel={handleCancel}
          />
        )}
      </StyledSection>
    </PagePadding>
  );
};

export default CreatePlaceholderPage;
